import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { instituteService } from '../services/instituteApi';

const InstituteContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InstituteProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [institute, setInstitute] = useState(null);
  const [branding, setBranding] = useState(null);
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultDocumentTitle] = useState(() => document.title || "Q-TRAN LMS");
  const [defaultFaviconHref] = useState(() => {
    const favicon = document.querySelector("link[rel='icon'], link[rel='shortcut icon']");
    return favicon?.getAttribute('href') || '/fav-icon.png';
  });

  // Resolve institute from URL (public context) or authenticated institute admin
  useEffect(() => {
    loadInstituteContext();
  }, [user, authLoading]);

  // Apply branding when it changes
  useEffect(() => {
    if (branding) {
      applyBranding(branding);
    }
  }, [branding]);

  const loadInstituteContext = async () => {
    if (authLoading) {
      return;
    }

    const isPlatformSuperadmin =
      (user?.role === 'superadmin' || user?.role === 'admin') &&
      (user?.institute_id === null || user?.institute_id === undefined);

    if (isPlatformSuperadmin) {
      clearInstituteContext();
      setLoading(false);
      return;
    }

    const role = (user?.role || '').toLowerCase();
    const isInstituteStaff = ['admin', 'instructor', 'support'].includes(role);

    if (user?.institute_id && isInstituteStaff) {
      await resolveInstituteFromAuthenticatedUser();
      return;
    }

    await resolveInstituteFromUrl();
  };

  const resolveInstituteFromAuthenticatedUser = async () => {
    setLoading(true);
    try {
      const response = await instituteService.getMyInstitute();
      const instituteData = response?.data?.institute || null;
      const brandingData = response?.data?.branding || null;

      setInstitute(instituteData);
      setBranding(brandingData);

      const instituteSlug = instituteData?.slug || instituteData?.subdomain;
      if (instituteSlug) {
        localStorage.setItem('instituteSlug', instituteSlug);
        axios.defaults.headers.common['X-Institute-Slug'] = instituteSlug;
        try {
          const res = await axios.get(`${API}/institutes/resolve/${instituteSlug}?type=slug`);
          if (res.data?.courses) {
            setCourses(res.data.courses);
          }
          if (res.data?.exams) {
            setExams(res.data.exams);
          }
        } catch (e) {
          // ignore
        }
      }
    } catch (error) {
      console.log('Authenticated institute context resolution failed');
      clearInstituteContext();
    } finally {
      setLoading(false);
    }
  };

  const resolveInstituteFromUrl = async () => {
    try {
      // Check for institute slug in different places
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      
      let instituteSlug = searchParams.get('institute') || searchParams.get('inst');
      
      // Check subdomain (e.g., tech-academy.navipro.com)
      if (!instituteSlug) {
        const parts = hostname.split('.');
        if (parts.length >= 3 && !['www', 'api', 'app'].includes(parts[0])) {
          instituteSlug = parts[0];
        }
      }
      
      // Check path (e.g., /inst/tech-academy/...)
      if (!instituteSlug && pathname.startsWith('/inst/')) {
        const pathParts = pathname.split('/');
        if (pathParts.length >= 3) {
          instituteSlug = pathParts[2];
        }
      }
      
      // Check localStorage (for persistence after login)
      if (!instituteSlug) {
        instituteSlug = localStorage.getItem('instituteSlug');
      }

      // Fallback
      if (!instituteSlug) {
        instituteSlug = process.env.REACT_APP_DEFAULT_INSTITUTE_SLUG || 'default';
      }
      
      if (instituteSlug) {
        const response = await axios.get(`${API}/institutes/resolve/${instituteSlug}?type=slug`);
        setInstitute(response.data.institute);
        setBranding(response.data.branding);
        setCourses(response.data.courses || []);
        setExams(response.data.exams || []);
        const resolvedSlug = response.data.institute?.slug || instituteSlug;
        localStorage.setItem('instituteSlug', resolvedSlug);
        
        // Set default header for all API requests
        axios.defaults.headers.common['X-Institute-Slug'] = resolvedSlug;
      }
    } catch (error) {
      console.log('No institute context or resolution failed');
    } finally {
      setLoading(false);
    }
  };

  const hexToHsl = (hex) => {
    if (!hex || typeof hex !== 'string') return null;
    const normalized = hex.trim();
    const match = normalized.match(/^#([A-Fa-f0-9]{6})$/);
    if (!match) return null;

    const raw = match[1];
    const r = parseInt(raw.slice(0, 2), 16) / 255;
    const g = parseInt(raw.slice(2, 4), 16) / 255;
    const b = parseInt(raw.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (delta !== 0) {
      s = delta / (1 - Math.abs(2 * l - 1));
      switch (max) {
        case r:
          h = ((g - b) / delta) % 6;
          break;
        case g:
          h = (b - r) / delta + 2;
          break;
        default:
          h = (r - g) / delta + 4;
          break;
      }
      h = Math.round(h * 60);
      if (h < 0) h += 360;
    }

    return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const toHslVariable = (value) => {
    if (!value || typeof value !== 'string') return null;
    const hslFromHex = hexToHsl(value);
    if (hslFromHex) return hslFromHex;
    return value.trim();
  };

  const setInstituteContext = (instituteData, brandingData) => {
    setInstitute(instituteData);
    setBranding(brandingData);
    if (instituteData?.slug) {
      localStorage.setItem('instituteSlug', instituteData.slug);
      axios.defaults.headers.common['X-Institute-Slug'] = instituteData.slug;
    }
  };

  const clearInstituteContext = () => {
    setInstitute(null);
    setBranding(null);
    setCourses([]);
    setExams([]);
    localStorage.removeItem('instituteSlug');
    delete axios.defaults.headers.common['X-Institute-Slug'];
    // Reset CSS variables to defaults
    resetBranding();
    document.title = defaultDocumentTitle;
    setFavicon(defaultFaviconHref);
  };

  const getOrCreateIconLink = (relValue) => {
    let link = document.querySelector(`link[rel='${relValue}']`);
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', relValue);
      document.head.appendChild(link);
    }
    return link;
  };

  const setFavicon = (href) => {
    const rawHref = href || defaultFaviconHref;
    const versionedHref = rawHref.includes('?')
      ? `${rawHref}&v=${Date.now()}`
      : `${rawHref}?v=${Date.now()}`;

    // Update common favicon tags so all browsers pick it up.
    const rels = ['icon', 'shortcut icon', 'apple-touch-icon'];
    rels.forEach((rel) => {
      const link = getOrCreateIconLink(rel);
      link.setAttribute('href', versionedHref);
    });

    // Also update any existing icon-like links with other rel variants.
    document.querySelectorAll("link[rel*='icon']").forEach((link) => {
      link.setAttribute('href', versionedHref);
    });
  };

  const applyPageMetadata = (brandingData) => {
    const siteTitle = (brandingData?.site_title || '').trim();
    if (siteTitle) {
      document.title = siteTitle;
    } else if (institute?.name) {
      document.title = institute.name;
    } else {
      document.title = defaultDocumentTitle;
    }

    if (brandingData?.favicon_url) {
      setFavicon(brandingData.favicon_url);
    } else {
      setFavicon(defaultFaviconHref);
    }
  };

  const applyBranding = (brandingData) => {
    const root = document.documentElement;
    
    // Apply colors
    if (brandingData.primary_color) {
      const primary = toHslVariable(brandingData.primary_color);
      if (primary) {
        root.style.setProperty('--primary', primary);
        root.style.setProperty('--ring', primary);
      }
    }
    if (brandingData.secondary_color) {
      const secondary = toHslVariable(brandingData.secondary_color);
      if (secondary) root.style.setProperty('--secondary', secondary);
    }
    if (brandingData.accent_color) {
      const accent = toHslVariable(brandingData.accent_color);
      if (accent) root.style.setProperty('--accent', accent);
    }
    if (brandingData.background_color) {
      const background = toHslVariable(brandingData.background_color);
      if (background) root.style.setProperty('--background', background);
    }
    if (brandingData.text_color) {
      const foreground = toHslVariable(brandingData.text_color);
      if (foreground) root.style.setProperty('--foreground', foreground);
    }
    
    // Apply font pairing: Headings (Outfit), Body/UI (IBM Plex Sans)
    const modernFallback = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    if (brandingData.font_family && brandingData.font_family.toLowerCase() !== 'inter') {
      root.style.setProperty('--font-family', `'${brandingData.font_family}', ${modernFallback}`);
      document.body.style.fontFamily = `'${brandingData.font_family}', ${modernFallback}`;
    } else {
      root.style.setProperty('--font-family', `'IBM Plex Sans', ${modernFallback}`);
      document.body.style.fontFamily = `'IBM Plex Sans', ${modernFallback}`;
    }

    if (brandingData.heading_font && brandingData.heading_font.toLowerCase() !== 'inter') {
      root.style.setProperty('--heading-font', `'${brandingData.heading_font}', ${modernFallback}`);
    } else {
      root.style.setProperty('--heading-font', `'Outfit', ${modernFallback}`);
    }
    
    // Apply custom CSS
    if (brandingData.custom_css) {
      let customStyleTag = document.getElementById('institute-custom-css');
      if (!customStyleTag) {
        customStyleTag = document.createElement('style');
        customStyleTag.id = 'institute-custom-css';
        document.head.appendChild(customStyleTag);
      }
      customStyleTag.textContent = brandingData.custom_css;
    }
    
    // Update favicon + title
    applyPageMetadata(brandingData);
  };

  const resetBranding = () => {
    const root = document.documentElement;
    root.style.removeProperty('--primary');
    root.style.removeProperty('--secondary');
    root.style.removeProperty('--accent');
    root.style.removeProperty('--background');
    root.style.removeProperty('--foreground');
    root.style.removeProperty('--font-family');
    root.style.removeProperty('--heading-font');
    root.style.removeProperty('--ring');
    document.body.style.fontFamily = '';
    document.title = defaultDocumentTitle;
    setFavicon(defaultFaviconHref);
    
    const customStyleTag = document.getElementById('institute-custom-css');
    if (customStyleTag) {
      customStyleTag.remove();
    }
  };

  const updateBranding = async (newBranding) => {
    try {
      const response = await axios.put(`${API}/institutes/my-institute/branding`, newBranding);
      setBranding(response.data);
      applyBranding(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update branding', error);
      throw error;
    }
  };

  useEffect(() => {
    if (branding || institute) {
      applyPageMetadata(branding || {});
      return;
    }
    document.title = defaultDocumentTitle;
    setFavicon(defaultFaviconHref);
  }, [branding, institute, defaultDocumentTitle, defaultFaviconHref]);

  return (
    <InstituteContext.Provider value={{
      institute,
      branding,
      courses,
      setCourses,
      exams,
      setExams,
      loading,
      refreshInstitute: loadInstituteContext,
      setInstituteContext,
      clearInstituteContext,
      updateBranding,
      applyBranding
    }}>
      {children}
    </InstituteContext.Provider>
  );
};

export const useInstitute = () => {
  const context = useContext(InstituteContext);
  if (!context) {
    throw new Error('useInstitute must be used within InstituteProvider');
  }
  return context;
};

export default InstituteContext;
